import { useCallback, useEffect, useState } from "react";
import { api, errMsg, download, itDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HardHat, Printer, Eye, CheckCircle, Warning } from "@phosphor-icons/react";

export default function MyPpe() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    try { setRows((await api.get("/safety/my-deliveries")).data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pending = rows.filter((r) => !r.signed);
  const signed = rows.filter((r) => r.signed);

  const card = (d) => (
    <div key={d.id} className="rounded-sm border border-border bg-card p-3"
      data-testid={`my-ppe-row-${d.progressive}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-head text-base font-bold">N. {d.progressive}</span>
        <span className="text-sm text-muted-foreground">{itDate(d.date)}</span>
        {d.signed
          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
              <CheckCircle size={14} weight="fill" /> Firmato {itDate(d.signed_date)}
            </span>
          : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
              <Warning size={14} /> DA FIRMARE — firma il modulo cartaceo in azienda
            </span>}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="h-8 rounded-sm" data-testid={`my-ppe-view-${d.progressive}`}
            onClick={() => setView(d)}><Eye size={14} className="mr-1.5" /> Consulta</Button>
          <Button size="sm" variant="outline" className="h-8 rounded-sm" data-testid={`my-ppe-pdf-${d.progressive}`}
            onClick={() => download(`/safety/deliveries/${d.id}/pdf`, `consegna_dpi_${d.progressive}.pdf`).catch((e) => toast.error(errMsg(e)))}>
            <Printer size={14} className="mr-1.5" /> PDF</Button>
        </div>
      </div>
      <div className="mt-1.5 text-xs text-muted-foreground">
        {(d.items || []).map((i) => `${i.description} x${i.quantity}`).join(" · ")}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="my-ppe-page">
      <div>
        <h1 className="font-head text-2xl font-bold tracking-tight sm:text-3xl">I miei DPI</h1>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          <HardHat size={15} /> I moduli di consegna DPI a tuo nome: consultali e scaricali in PDF.
        </p>
      </div>

      {pending.length > 0 && (
        <div className="space-y-2" data-testid="my-ppe-pending">
          <h2 className="text-base font-semibold text-amber-600 md:text-lg">Da firmare ({pending.length})</h2>
          {pending.map(card)}
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-base font-semibold md:text-lg">Moduli firmati ({signed.length})</h2>
        {signed.map(card)}
        {signed.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="my-ppe-empty-signed">
            Nessun modulo firmato
          </p>
        )}
      </div>

      {rows.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground" data-testid="my-ppe-empty">
          Non risultano moduli di consegna DPI a tuo nome
        </p>
      )}

      {view && (
        <Dialog open onOpenChange={() => setView(null)}>
          <DialogContent className="max-h-[88vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="my-ppe-view-dialog">
            <DialogHeader><DialogTitle className="font-head">Modulo n. {view.progressive} del {itDate(view.date)}</DialogTitle></DialogHeader>
            <div className="grid gap-3 text-sm sm:grid-cols-3">
              {[["Luogo", view.place || "—"], ["Consegnato da", view.delivered_by || "—"],
                ["Stato", view.signed ? `Firmato il ${itDate(view.signed_date)}` : "Da firmare"],
                ["Note", view.notes || "—"]].map(([l, v]) => (
                <div key={l}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</div>
                  <div className="mt-0.5">{v}</div>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="my-ppe-view-items">
                <thead className="bg-secondary"><tr>
                  {["Articolo", "Tipo", "Q.tà", "Taglia", "Sostituzione"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(view.items || []).map((i, idx) => (
                    <tr key={i.id || `${i.description}-${i.kind}-${i.size}-${idx}`} className="border-t border-border">
                      <td className="px-3 py-1.5">{i.description}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{i.kind}</td>
                      <td className="px-3 py-1.5 tabular-nums">{i.quantity}</td>
                      <td className="px-3 py-1.5">{i.size || "—"}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{i.replacement}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setView(null)} data-testid="my-ppe-view-close">Chiudi</Button>
              <Button className="rounded-sm font-semibold" data-testid="my-ppe-view-pdf"
                onClick={() => download(`/safety/deliveries/${view.id}/pdf`, `consegna_dpi_${view.progressive}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                <Printer size={15} className="mr-1.5" /> Scarica PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
